/**
 * CacheService Unificado
 * Sistema de cache com múltiplas camadas:
 * - Camada 1: Memory cache (mais rápido, volátil)
 * - Camada 2: localStorage (persistente, sobrevive a reloads)
 * - Integração com React Query para sincronização
 */

import { appStorage } from '@/lib/appStorage';

// Tipos
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl?: number; // TTL opcional em ms
}

interface CacheConfig {
  defaultTTL?: number; // TTL padrão em ms
  enablePersistence?: boolean; // Se deve persistir no localStorage
  enableMemory?: boolean; // Se deve usar cache em memória
}

// Configuração padrão
const DEFAULT_CONFIG: Required<CacheConfig> = {
  defaultTTL: 5 * 60 * 1000, // 5 minutos
  enablePersistence: true,
  enableMemory: true,
};

// Cache em memória (camada 1) - exportado para uso interno
export const memoryCache = new Map<string, CacheEntry<any>>();

// Prefixo para chaves no localStorage
const STORAGE_PREFIX = 'app_cache_';

/**
 * Verifica se uma entrada de cache está expirada
 */
function isExpired<T>(entry: CacheEntry<T> | null | undefined): boolean {
  if (!entry) return true;
  const ttl = entry.ttl || DEFAULT_CONFIG.defaultTTL;
  return Date.now() - entry.timestamp > ttl;
}

/**
 * Cria uma entrada de cache
 */
function createCacheEntry<T>(data: T, ttl?: number): CacheEntry<T> {
  return {
    data,
    timestamp: Date.now(),
    ttl,
  };
}

/**
 * Obtém chave formatada para localStorage
 */
function getStorageKey(key: string): string {
  return `${STORAGE_PREFIX}${key}`;
}

/**
 * Serviço de Cache Unificado
 */
class CacheService {
  private config: Required<CacheConfig>;

  constructor(config: CacheConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Obtém um valor do cache de forma síncrona (tenta memory primeiro, depois localStorage síncrono)
   */
  getSync<T>(key: string): T | null {
    // 1. Tenta memory cache primeiro
    if (this.config.enableMemory) {
      const memoryEntry = memoryCache.get(key);
      if (memoryEntry && !isExpired(memoryEntry)) {
        return memoryEntry.data as T;
      }
      // Remove se expirado
      if (memoryEntry && isExpired(memoryEntry)) {
        memoryCache.delete(key);
      }
    }

    // 2. Tenta localStorage (síncrono)
    if (this.config.enablePersistence) {
      try {
        const storageKey = getStorageKey(key);
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const entry: CacheEntry<T> = JSON.parse(stored);
          if (!isExpired(entry)) {
            // Restaura no memory cache
            if (this.config.enableMemory) {
              memoryCache.set(key, entry);
            }
            return entry.data;
          } else {
            // Remove se expirado
            localStorage.removeItem(storageKey);
          }
        }
      } catch (error) {
        console.warn(`[CacheService] Erro ao ler do localStorage para ${key}:`, error);
      }
    }

    return null;
  }

  /**
   * Obtém um valor do cache (tenta memory primeiro, depois localStorage)
   */
  async get<T>(key: string): Promise<T | null> {
    // Tenta síncrono primeiro
    const syncResult = this.getSync<T>(key);
    if (syncResult !== null) {
      return syncResult;
    }

    // Se não encontrou síncrono, tenta assíncrono (para Capacitor Preferences)
    if (this.config.enablePersistence) {
      try {
        const storageKey = getStorageKey(key);
        const stored = await appStorage.getItem(storageKey);
        if (stored) {
          const entry: CacheEntry<T> = JSON.parse(stored);
          if (!isExpired(entry)) {
            // Restaura no memory cache
            if (this.config.enableMemory) {
              memoryCache.set(key, entry);
            }
            return entry.data;
          } else {
            // Remove se expirado
            await appStorage.removeItem(storageKey);
          }
        }
      } catch (error) {
        console.warn(`[CacheService] Erro ao ler do appStorage para ${key}:`, error);
      }
    }

    return null;
  }

  /**
   * Armazena um valor no cache
   */
  async set<T>(key: string, value: T, ttl?: number): Promise<void> {
    const entry = createCacheEntry(value, ttl);

    // 1. Armazena em memory
    if (this.config.enableMemory) {
      memoryCache.set(key, entry);
    }

    // 2. Armazena no localStorage
    if (this.config.enablePersistence) {
      try {
        const storageKey = getStorageKey(key);
        await appStorage.setItem(storageKey, JSON.stringify(entry));
      } catch (error) {
        console.warn(`[CacheService] Erro ao salvar no localStorage para ${key}:`, error);
        // Se localStorage estiver cheio, tenta limpar cache antigo
        await this.cleanExpired();
      }
    }
  }

  /**
   * Remove uma entrada do cache
   */
  async remove(key: string): Promise<void> {
    // Remove de memory
    if (this.config.enableMemory) {
      memoryCache.delete(key);
    }

    // Remove do localStorage
    if (this.config.enablePersistence) {
      try {
        const storageKey = getStorageKey(key);
        await appStorage.removeItem(storageKey);
      } catch (error) {
        console.warn(`[CacheService] Erro ao remover do localStorage para ${key}:`, error);
      }
    }
  }

  /**
   * Limpa todo o cache
   */
  async clear(): Promise<void> {
    // Limpa memory
    if (this.config.enableMemory) {
      memoryCache.clear();
    }

    // Limpa localStorage (apenas chaves de cache)
    if (this.config.enablePersistence) {
      try {
        // Obtém todas as chaves do localStorage
        const allKeys = Object.keys(localStorage);
        const cacheKeys = allKeys.filter(key => key.startsWith(STORAGE_PREFIX));
        
        // Remove cada chave
        for (const key of cacheKeys) {
          localStorage.removeItem(key);
        }
      } catch (error) {
        console.warn('[CacheService] Erro ao limpar localStorage:', error);
      }
    }
  }

  /**
   * Limpa apenas entradas expiradas
   */
  async cleanExpired(): Promise<void> {
    const now = Date.now();

    // Limpa memory cache expirado
    if (this.config.enableMemory) {
      for (const [key, entry] of memoryCache.entries()) {
        if (isExpired(entry)) {
          memoryCache.delete(key);
        }
      }
    }

    // Limpa localStorage expirado
    if (this.config.enablePersistence) {
      try {
        const allKeys = Object.keys(localStorage);
        const cacheKeys = allKeys.filter(key => key.startsWith(STORAGE_PREFIX));
        
        for (const key of cacheKeys) {
          try {
            const stored = localStorage.getItem(key);
            if (stored) {
              const entry: CacheEntry<any> = JSON.parse(stored);
              if (isExpired(entry)) {
                localStorage.removeItem(key);
              }
            }
          } catch {
            // Se não conseguir parsear, remove
            localStorage.removeItem(key);
          }
        }
      } catch (error) {
        console.warn('[CacheService] Erro ao limpar cache expirado:', error);
      }
    }
  }

  /**
   * Obtém todas as chaves do cache
   */
  getKeys(): string[] {
    const keys = new Set<string>();

    // Memory keys
    if (this.config.enableMemory) {
      memoryCache.forEach((_, key) => keys.add(key));
    }

    // localStorage keys (remove prefixo)
    if (this.config.enablePersistence) {
      try {
        const allKeys = Object.keys(localStorage);
        allKeys
          .filter(key => key.startsWith(STORAGE_PREFIX))
          .forEach(key => {
            keys.add(key.replace(STORAGE_PREFIX, ''));
          });
      } catch (error) {
        console.warn('[CacheService] Erro ao obter chaves do localStorage:', error);
      }
    }

    return Array.from(keys);
  }

  /**
   * Obtém o tamanho do cache
   */
  getSize(): number {
    return this.getKeys().length;
  }

  /**
   * Invalida cache por padrão (útil para invalidar grupos de cache)
   */
  async invalidatePattern(pattern: string | RegExp): Promise<void> {
    const keys = this.getKeys();
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
    
    const keysToRemove = keys.filter(key => regex.test(key));
    await Promise.all(keysToRemove.map(key => this.remove(key)));
  }
}

// Instância singleton do cache service
export const cacheService = new CacheService();

// Chaves de cache comuns (mantidas para compatibilidade)
export const CACHE_KEYS = {
  SONGS: 'songs',
  CATEGORIES: 'categories',
  FAVORITES: 'favorites',
  SEARCH: 'search',
  DAILY_VERSES: 'daily_verses',
  JOURNEY_SONGS: 'journey_songs',
  PLAYLIST: 'playlist',
} as const;

// Funções de conveniência (mantidas para compatibilidade com código existente)
// Versão síncrona para compatibilidade com código existente
export function getFromCache<T>(key: string): T | undefined {
  const result = cacheService.getSync<T>(key);
  return result !== null ? result : undefined;
}

// Versão assíncrona para novo código
export async function getFromCacheAsync<T>(key: string): Promise<T | null> {
  return cacheService.get<T>(key);
}

export async function setInCache<T>(key: string, value: T, ttl?: number): Promise<void> {
  return cacheService.set(key, value, ttl);
}

export async function removeFromCache(key: string): Promise<void> {
  return cacheService.remove(key);
}

export async function clearCache(): Promise<void> {
  return cacheService.clear();
}

export function getCacheKeys(): string[] {
  return cacheService.getKeys();
}

export function getCacheSize(): number {
  return cacheService.getSize();
}

// Funções de invalidação específicas (mantidas para compatibilidade)
export async function invalidateSongsCache(): Promise<void> {
  await cacheService.invalidatePattern(/^songs|^song_|^get_all_songs/);
}

export async function invalidateSearchCache(): Promise<void> {
  await cacheService.invalidatePattern(/^search_/);
}

// Exporta a instância e a classe para uso avançado
export { CacheService };

